import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { TrustBand } from "@/components/site/TrustBand";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { aeoGeo } from "@/lib/seo/schema";
import {
  answerSeoTitle,
  getAnswerBySlug,
  getAnswers,
  getAnswerSlug,
  getQuestionSlugs,
  toHowToJsonLd,
  toQaPageJsonLd,
} from "@/lib/aeo/answers";

const VALID_SLUGS = getQuestionSlugs("en");

export const Route = createFileRoute("/en_/questions_/$slug")({
  beforeLoad: ({ params }) => {
    if (!VALID_SLUGS.includes(params.slug)) throw notFound();
  },
  loader: ({ params }) => {
    const answer = getAnswerBySlug(params.slug, "en")!;
    return { answer };
  },
  head: ({ loaderData, params }) => {
    const a = loaderData?.answer;
    if (!a) return { meta: [{ title: "Question — Fenny" }] };
    const url = `https://fenny.se/en/questions/${params.slug}`;
    const title = answerSeoTitle(a);
    const description = a.metaDescription ?? a.shortAnswer;
    const primary =
      a.schemaType === "HowTo"
        ? toHowToJsonLd(a.id, url) ?? toQaPageJsonLd(a.id, url)
        : toQaPageJsonLd(a.id, url);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: a.metaTitle ?? a.question },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: `/en/questions/${params.slug}`, name: a.question, description: a.shortAnswer, question: a.question, answer: a.shortAnswer })) },
        ...(primary
          ? [{ type: "application/ld+json", children: JSON.stringify(primary) }]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Questions & answers", item: "/en/questions" },
              { "@type": "ListItem", position: 2, name: a.question, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: EnQuestionPage,
});

function EnQuestionPage() {
  const { answer: a } = Route.useLoaderData();
  const paragraphs = a.longAnswer.split("\n\n");
  const related = getAnswers(a.relatedQuestions);

  return (
    <div>
      <Section className="pt-16 md:pt-24">
        <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <Link to="/en/questions" className="transition-colors hover:text-foreground">
            Questions & answers
          </Link>
          <span aria-hidden>/</span>
          <span className="text-foreground/80">{a.question}</span>
        </nav>

        <article className="max-w-3xl">
          <h1 className="text-balance text-3xl font-semibold leading-tight md:text-5xl">
            {a.question}
          </h1>

          <QuickAnswer answerId={a.id} tone="mint" label="Quick answer" />

          <div className="mt-8 space-y-5 text-base leading-relaxed text-foreground/85 md:text-lg">
            {paragraphs.map((p: string, i: number) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {a.relatedService && (
            <div className="mt-10">
              <Link
                to={a.relatedPage}
                className="group inline-flex items-center gap-2 rounded-full border border-mint/40 bg-mint/10 px-5 py-3 text-sm font-medium text-foreground transition-colors hover:border-mint/60"
              >
                Learn more about {a.relatedService}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          )}
        </article>
      </Section>

      {related.length > 0 && (
        <Section className="pt-0">
          <div className="max-w-3xl">
            <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/60">
              Related questions
            </p>
            <ul className="divide-y divide-foreground/10 border-y border-foreground/10">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/en/questions/$slug"
                    params={{ slug: getAnswerSlug(r) }}
                    className="group flex items-center justify-between gap-4 py-4 text-base text-foreground/85 transition-colors hover:text-foreground"
                  >
                    <span>{r.question}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-foreground/50 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}

      <Section className="py-6">
        <div className="max-w-3xl">
          <RelatedLinksRail sourcePath={a.relatedPage} title="Explore further" />
        </div>
      </Section>

      <SignatureCta
        eyebrow="Still unsure?"
        title="Book 30 minutes."
        body="Free strategy call. We give you a straight answer on what video would cost and which model fits."
        tone="mint"
      />

      <TrustBand ctaLocation={`en_questions_${a.id}_trust_band`} />
    </div>
  );
}
